/* eslint-disable @typescript-eslint/no-var-requires */
import React, { ReactElement, useCallback, useRef } from 'react';
import isEqual from 'lodash.isequal';
import uaParser from 'ua-parser-js';
import { isServer } from 'b2b-apps/shop/common/utils/env';
import { useProductPdfActions, useProductPdfData } from 'b2b-apps/shop/product/hooks';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

enum FIELD_NAME {
  additionalComments = 'additionalComments',
  additionalImages = 'additionalImages',
  description = 'description',
  energyLabel = 'energyLabel',
  fixedPrices = 'fixedPrices',
  fixedPricesTaxValue = 'fixedPricesTaxValue',
  listPrice = 'listPrice',
  listPriceWithoutVat = 'listPriceWithoutVat',
  mainImage = 'mainImage',
  manufacturerNumber = 'manufacturerNumber',
  offer = 'offer',
  offerPriceValue = 'offerPriceValue',
  offerValue = 'offerValue',
  productName = 'productName',
  shortInfo = 'shortInfo',
  sku = 'sku',
  specification = 'specification',
  userPrice = 'userPrice',
  userPriceWithoutVat = 'userPriceWithoutVat',
  warrantyInfo = 'warrantyInfo',
  withoutVAT = 'withoutVAT',
  withVAT = 'withVAT',
}

const browser = uaParser().browser;
const isIE = browser.name === 'IE';
const isOldEdge = browser.name === 'Edge' && parseInt(`${browser?.version?.split('.')[0]}`, 10) <= 18;
const isPrintAllowed = !isIE && !isOldEdge;

const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { product } = props;
    const PDFContentRef = useRef<HTMLDivElement | null>(null);
    const {
      printPdf,
      downloadPdf,
    } = useProductPdfActions();
    const {
      printPdf: printData,
      downloadPdf: downloadData,
    } = useProductPdfData();
    const isPrintInProgress = printData.wasStarted && printData.isLoading;
    const isDownloadInProgress = downloadData.wasStarted && downloadData.isLoading;

    const getPDFContent = () => {
      const styles = !isServer
        // eslint-disable-next-line @typescript-eslint/no-var-requires, import/no-webpack-loader-syntax
        ? require('!!css-loader!../../PDFContent/stylesLoader/loader.css').default.toString()
        : '';

      return `
        <html>
          <head>
            <meta charset='UTF-8'>
          </head>
          <body>
            <style>
              ${styles}
            </style>
            ${(PDFContentRef.current && PDFContentRef.current.innerHTML) || ''}
          </body>
        </html>
      `;
    };

    const printPDF = useCallback(
      () => printPdf(product.sku, getPDFContent()),
      [
        printPdf,
        product.sku,
      ],
    );

    const generatePDF = useCallback(
      () => downloadPdf(product.sku, getPDFContent()),
      [downloadPdf, product.sku],
    );

    const injectedProps: InjectedProps = {
      isPrintAllowed,
      printPDF,
      generatePDF,
      PDFContentRef,
      isPrintInProgress,
      isDownloadInProgress,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return React.memo(WithContainer, (prevProps, nextProps) => isEqual(prevProps, nextProps));
};

export { FIELD_NAME, withContainer };
